import * as THREE from 'three';
import type { WorldSnapshot } from '../../../shared/network/protocol.js';
import { firstUnseenEvent } from '../../../shared/network/visual-history.js';
import { weaponVisual } from '../../../content/equipment/visuals.js';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { createProjectileVisual, poseProjectile } from './projectile-visual.js';
import { hasOwnFlight, isShepherdCue, isShepherdShot, isSpellAttack } from '../../../content/spells/effects.js';
import { SpellEffects } from './spell-effects.js';
import { ShepherdEffects } from './shepherd-effects.js';
import { BerserkerEffects } from './berserker-effects.js';
import { OathswornEffects } from './oathsworn-effects.js';
import type { UnitAnimator } from '../entities/unit-animator.js';
import { BloodEffects } from './blood-effects.js';
import { ShadowBlastEffects } from './shadow-blast-effects.js';
import { ShadowmancerEffects } from './shadowmancer-effects.js';
import { isMageCue, MageEffects } from './mage-effects.js';
import { ScreenShake, shakeFor } from '../camera/screen-shake.js';
import { hitStrength } from '../entities/character-hit.js';
import type { SeveredHead } from './blood-model.js';
import { createQuarryVisual, createSkillEffect, updateQuarryVisual, type SkillEffect } from './skill-effects.js';
import { effectMaterial, effectParticles, lightningVisual, shadeEffect } from './effect-surfaces.js';
import { TargetArrows } from './target-arrows.js';
import { OverheadMessages } from '../overhead-messages.js';
import { graphicsOptions } from '../graphics-options.js';
import { chargeMessage, combatMessage, judgmentMessage } from './combat-message.js';
import { smokePuff } from './smoke-puff.js';
import { sneakReveal } from './sneak-reveal.js';
import { sneakShimmer } from './sneak-shimmer.js';
import { retaliationDistortion } from './retaliation-distortion.js';
import { distortionShell } from './howl-wave.js';
import { persistentStatusLabels, StatusEffects } from './status-effects.js';
import type { CastState } from '../../../shared/game/class-types.js';
import { capsuleHit, intersectImpactSurface } from './impact-bounds.js';
import { presenceLead } from '../entities/unit-reveal.js';
import { chargeTrailWarmup } from '../entities/charge-trail.js';
import { SpellChannelVisual } from './spell-channel.js';
import { schools } from '../../../content/spells/schools.js';

type ActiveSkill = {
  effect: SkillEffect;
  event: CombatVisualEvent;
  started: number;
  /** The target has appeared in the buffered snapshots; a revealed unit arrives in them after its own cue. */
  seen?: boolean;
  from: THREE.Vector3;
  to: THREE.Vector3;
  origin?: THREE.Vector3;
};

/**
 * Where a line through `from` along `direction` first enters the figure's solid meshes, if it does. Skinned surfaces are met as their
 * bones' capsules (`capsuleHit`): a lodged arrow sits within a few centimetres of the drawn skin, and a hit never poses the body.
 */
export function skinAlong(figure: THREE.Object3D, from: THREE.Vector3, direction: THREE.Vector3, reach: number): THREE.Vector3 | undefined {
  const ray = new THREE.Raycaster(from.clone().addScaledVector(direction, -reach), direction, 0, reach * 2);
  const rigid: THREE.Object3D[] = [];
  let nearest = Infinity;
  figure.traverse(node => {
    const material = node instanceof THREE.Mesh ? node.material as THREE.Material | THREE.Material[] : undefined;
    if (!material || !node.visible || [material].flat().some(m => !m.visible || !m.colorWrite || m.transparent || m.opacity < 1)) return;
    for (let up = node.parent; up && up !== figure; up = up.parent) if (!up.visible || up.userData.actorId !== undefined) return;
    if (node instanceof THREE.SkinnedMesh) nearest = Math.min(nearest, capsuleHit(node, ray.ray, ray.far));
    else rigid.push(node);
  });
  const hits: THREE.Intersection[] = [];
  for (const solid of rigid) intersectImpactSurface(solid, ray, hits);
  if (hits[0] && hits[0].distance < nearest) return hits[0].point;
  return Number.isFinite(nearest) ? ray.ray.at(nearest, new THREE.Vector3()) : undefined;
}

/** Whether `object` hangs anywhere below `ancestor`. */
function isWithin(object: THREE.Object3D, ancestor: THREE.Object3D | undefined) {
  for (let node = object.parent; node; node = node.parent) if (node === ancestor) return true;
  return false;
}

/**
 * The bone of any skinned figure nearest a world point, judged against each bone's length (joint to child joint; a tie goes to the deeper bone) so a
 * long neck or wing is hit along its span, not only at its root. Needs nothing from the creature beyond its skeleton.
 */
export function nearestBone(figure: THREE.Object3D, point: THREE.Vector3): THREE.Bone | undefined {
  const start = new THREE.Vector3(), end = new THREE.Vector3(), along = new THREE.Vector3(), line = new THREE.Line3();
  let best: THREE.Bone | undefined, bestDistance = Infinity;
  figure.traverse(node => {
    if (!(node instanceof THREE.Bone)) return;
    node.getWorldPosition(start);
    const children = node.children.filter((child): child is THREE.Bone => child instanceof THREE.Bone);
    for (const child of children.length ? children : [node]) {
      child.getWorldPosition(end); line.set(start, end);
      const distance = line.closestPointToPoint(point, true, along).distanceToSquared(point);
      if (distance <= bestDistance) { bestDistance = distance; best = node; }
    }
  });
  return best;
}

/** Presentation only: damage and projectile arrival are decided by the server. */
export class CombatEffects {
  get visualObjects(): THREE.Object3D[] { return [...this.arrows.values(), ...[...this.stuck.values()].flat(),
    ...this.skills.map(skill => skill.effect.group), ...this.quarry.values(), ...this.spells.visualObjects, ...this.shadowBlasts.visualObjects, ...this.shadowmancer.visualObjects, ...this.blood.visualObjects, ...this.targetArrows.visualObjects, ...this.statuses.visualObjects, ...this.shepherd.visualObjects, ...this.berserker.visualObjects, ...this.mage.visualObjects, ...this.oathsworn.visualObjects]; }
  private lastId = 0;
  private revision = -1;
  private initialized = false;
  private skills: ActiveSkill[] = [];
  private skillWarmup = new THREE.Group();
  private frameTime = 0;
  private powerLights: NonNullable<SkillEffect['lights']> = [];
  private channelOrigins = new Map<string, { position: THREE.Vector3; at: number }>();
  /** Lightning shots already shown as a discharge: an electrical attack strikes at once rather than flying. */
  private discharged = new Set<number>();
  private sustained = new Set<string>();
  private messages: OverheadMessages;
  private arrows = new Map<number, THREE.Group>();
  /** Ranged impacts the server reported, waiting for their arrow to finish flying on screen (events run ahead of the interpolated frame). */
  private hits: { actorId: string; targetId: string; at: number }[] = [];
  /** Arrows lodged in a figure: children of that figure, so they travel, fall and hide with it. */
  private stuck = new Map<string, THREE.Group[]>();
  private wasDead = new Map<string, boolean>();
  private quarry = new Map<string, THREE.Group>();
  private positions: ReadonlyMap<string, THREE.Object3D> = new Map();
  private heights: Map<string, number>;
  private spells: SpellEffects;
  private shepherd: ShepherdEffects;
  private berserker: BerserkerEffects;
  private oathsworn: OathswornEffects;
  private mage: MageEffects;
  set onPreviewLand(land: (abilityId: string, at: { x: number; z: number }) => void) { this.spells.onPreviewLand = land; }
  get spellLights() { return this.spells.lights.concat(this.shadowBlasts.lights, this.powerLights, this.shepherd.lights, this.berserker.lights, this.mage.lights, this.oathsworn.lights); }
  prepareSpells() { this.spells.prepare(); this.shadowBlasts.prepare(); this.scene.add(this.skillWarmup); }
  spellsPrepared() { this.spells.prepared(); this.shadowBlasts.prepared(); this.skillWarmup.removeFromParent(); }
  private targetArrows: TargetArrows;
  private statuses: StatusEffects;
  private blood: BloodEffects;
  private shadowBlasts: ShadowBlastEffects;
  private shadowmancer: ShadowmancerEffects;
  private lastRender = performance.now();
  /** Heavy blows add to it; the scene applies it to the camera around each render. */
  readonly shake = new ScreenShake();
  /** Takes a unit's head off its figure; the game scene supplies it, since the figures live there. */
  severHead: (id: string) => SeveredHead | undefined = () => undefined;
  constructor(private scene: THREE.Scene, private host: HTMLElement, definitions: UnitDefinition[] = [], private groundHeight: (x: number, z: number) => number = () => 0) {
      this.heights = new Map(definitions.map(unit => [unit.id, unit.appearance?.height ?? 2]));
    this.spells = new SpellEffects(scene, this.heights, groundHeight);
    this.shadowBlasts = new ShadowBlastEffects(scene, this.heights, groundHeight);
    this.shadowmancer = new ShadowmancerEffects(scene, this.heights, groundHeight);
    this.shepherd = new ShepherdEffects(scene, this.heights, groundHeight);
    this.berserker = new BerserkerEffects(scene, this.heights);
    this.oathsworn = new OathswornEffects(scene, this.heights);
    this.mage = new MageEffects(scene, this.heights, groundHeight);
    this.targetArrows = new TargetArrows(scene, this.heights);
    this.statuses = new StatusEffects(scene, this.heights);
    this.messages = new OverheadMessages(host, this.heights);
    this.blood = new BloodEffects(scene, groundHeight);
    this.skillWarmup.name = 'skill-shader-warmup';
    this.skillWarmup.add(new THREE.Mesh(new THREE.SphereGeometry(.1), effectMaterial(0xffffff)), effectParticles(0xffffff, 1, .1, .1));
    // Smoke and see-through distortion use their own materials, one program per ripple: compile them up front too, so a
    // first summon, howl, reveal or taunt does not stutter.
    this.skillWarmup.add(smokePuff().group, distortionShell('warmup-distortion').mesh, sneakReveal(2, .5).group, retaliationDistortion().group, sneakShimmer().group);
    // So do the visuals built on demand mid-fight: arrows and javelins, target arrows, stains, charge trails and every school's channel.
    this.skillWarmup.add(...['shortbow', 'javelins', 'bandit-arc', 'crossbow', 'handheld-ballista', 'throwing-hammers'].map(createProjectileVisual), this.targetArrows.warmup(), this.blood.warmup(), ...chargeTrailWarmup(),
      ...schools.map(school => new SpellChannelVisual(school).group));
  }
  /** A seat's hero changed; hit sparks and numbers follow its new height. */
  setHeight(id: string, height: number) { this.heights.set(id, height); }
  private remove(object: THREE.Object3D) {
    object.removeFromParent();
    const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
    object.traverse(child => { if (child instanceof THREE.Mesh || child instanceof THREE.Line || child instanceof THREE.Points) { geometries.add(child.geometry); for (const material of Array.isArray(child.material) ? child.material : [child.material]) materials.add(material); } });
    // Shared parts (projectiles) outlive any one effect: disposing them would also release their compiled shader.
    for (const geometry of geometries) if (!geometry.userData.shared) geometry.dispose();
    for (const material of materials) if (!material.userData.shared) material.dispose();
  }
  clear() {
    this.powerLights.length = 0;
    this.channelOrigins.clear(); this.discharged.clear(); this.sustained.clear();
    this.spells.clear(); this.shadowBlasts.clear(); this.shadowmancer.clear();
    this.shepherd.clear(); this.berserker.clear(); this.mage.clear(); this.oathsworn.clear();
    this.targetArrows.clear();
    this.statuses.clear();
    this.messages.clear();
    for (const arrow of this.arrows.values()) this.remove(arrow);
    for (const arrows of this.stuck.values()) for (const arrow of arrows) this.remove(arrow);
    this.hits = []; this.stuck.clear(); this.wasDead.clear();
    for (const skill of this.skills) this.remove(skill.effect.group);
    for (const mark of this.quarry.values()) this.remove(mark);
    this.skills = []; this.arrows.clear(); this.quarry.clear(); this.positions = new Map();
    this.blood.clear(); this.shake.clear();
  }
  /** Blood that outlives its scene: removes the meshes the effects keep for themselves. */
  dispose() { this.clear(); this.remove(this.skillWarmup); this.spells.dispose(); this.shadowBlasts.dispose(); this.shadowmancer.dispose(); this.mage.dispose(); this.blood.dispose(); }
  /** Authoring preview: sends no gameplay commands or synthetic network events. */
  preview(event: CombatVisualEvent, snapshot: WorldSnapshot, origin?: THREE.Vector3, at = this.frameTime) {
    if (event.abilityId === 'breath-of-life' || isSpellAttack(event.abilityId ?? '')) {
      if (origin) this.spells.preview(event, origin, at); return;
    }
    if (event.abilityId === 'preview-lightning' && event.kind === 'miss') {
      this.strayDischarge(event.actorId, event.targetId, event.from, event.to, snapshot, at, origin, Math.round(at * 10)); return;
    }
    if (event.abilityId === 'preview-lightning') {
      const group = lightningVisual(), direction = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0), end = new THREE.Vector3();
      const targetHeight = this.heights.get(event.targetId) ?? 2;
      const effect: SkillEffect = { group, duration: 750, update(p, from, to, time = 0, ppm = 40) {
        end.copy(to).setY(targetHeight * .55);
        group.position.copy(from).lerp(end, p); if (!origin) group.position.y += (1 - p) * 1.1;
        direction.copy(end).sub(from); if (direction.lengthSq() < .00001) direction.set(0, 0, 1);
        group.quaternion.setFromUnitVectors(up, direction.normalize()); shadeEffect(group, time, 1, p, ppm);
      } };
      this.scene.add(group); this.skills.push({ effect, event, started: at, from: new THREE.Vector3(), to: new THREE.Vector3(), origin: origin?.clone() });
      this.addSkill({ ...event, kind: 'impact', damageType: 'lightning', abilityId: undefined }, snapshot, at + .75); return;
    }
    const status = event.kind === 'condition' ? persistentStatusLabels.get(event.label ?? '') : undefined;
    if (status) { this.statuses.preview(event.targetId, status, at + 4); return; }
    this.addSkill(event, snapshot, at, origin);
    if (event.kind === 'mark') {
      const target = [...snapshot.heroes, ...snapshot.enemies].find(unit => unit.id === event.targetId);
      const group = createQuarryVisual(target?.radius ?? .5, this.heights.get(event.targetId) ?? 2);
      const effect: SkillEffect = { group, duration: 5000, update(p, _from, to, time = 0, ppm = 40) {
        group.position.copy(to); updateQuarryVisual(group, time, ppm); shadeEffect(group, time, Math.min(1, p * 8, (1 - p) * 5) * .6, p, ppm);
      } };
      this.scene.add(group); this.skills.push({ effect, event, started: at, from: new THREE.Vector3(), to: new THREE.Vector3() });
    }
    while (this.skills.length > 48) this.remove(this.skills.shift()!.effect.group);
  }
  /**
   * A missed lightning shot: the discharge goes to ground beside its target instead of into it. Its end is a fixed point in the world (no
   * unit id), so it neither follows the target nor ends with it.
   */
  private strayDischarge(actorId: string, targetId: string, from: { x: number; z: number }, to: { x: number; z: number }, snapshot: WorldSnapshot, started: number, origin?: THREE.Vector3, seed = 0) {
    const target = [...snapshot.heroes, ...snapshot.enemies].find(unit => unit.id === targetId);
    const dx = to.x - from.x, dz = to.z - from.z, length = Math.hypot(dx, dz) || 1, side = seed % 2 ? 1 : -1;
    const wide = (target?.radius ?? .5) + .55, beyond = .35;
    const ground = { x: to.x + dx / length * beyond - dz / length * wide * side, z: to.z + dz / length * beyond + dx / length * wide * side };
    this.addSkill({ id: 0, kind: 'arc', actorId, targetId: '', from: { ...from }, to: ground, damage: 0, critical: false }, snapshot, started, origin, .15);
  }
  private addSkill(event: CombatVisualEvent, snapshot: WorldSnapshot, started: number, origin?: THREE.Vector3, targetHeight?: number) {
    const target = [...snapshot.heroes, ...snapshot.enemies].find(unit => unit.id === event.targetId);
    const skill = createSkillEffect(event, { actorHeight: this.heights.get(event.actorId) ?? 2,
      targetHeight: targetHeight ?? this.heights.get(event.targetId) ?? 2, targetRadius: target?.radius ?? .5, launchAtOrb: !!origin });
    if (!skill) return;
    const from = new THREE.Vector3(event.from.x, this.groundHeight(event.from.x, event.from.z), event.from.z), to = new THREE.Vector3(event.to.x, this.groundHeight(event.to.x, event.to.z), event.to.z);
    if (origin) from.copy(origin);
    skill.update(0, from, to); this.scene.add(skill.group); this.skills.push({ effect: skill, event, started, from, to, origin: origin?.clone() });
    while (this.skills.length > 48) this.remove(this.skills.shift()!.effect.group);
  }
  receive(snapshot: WorldSnapshot) {
    if (this.revision !== snapshot.revision) { this.clear(); this.revision = snapshot.revision; this.initialized = false; this.lastId = 0; }
    if (!this.initialized) { this.lastId = firstUnseenEvent(snapshot); this.initialized = true; }
    for (const event of snapshot.visualEvents) {
      if (event.id <= this.lastId) continue;
      this.lastId = event.id;
      this.spells.receive(event, snapshot.gameTime); this.shadowBlasts.receive(event, snapshot.gameTime); this.shadowmancer.receive(event, snapshot.gameTime);
      this.shepherd.receive(event, snapshot.gameTime); this.berserker.receive(event, snapshot.gameTime); this.oathsworn.receive(event, snapshot.gameTime); this.mage.receive(event, snapshot.gameTime);

      const shake = shakeFor(event);
      if (shake) this.shake.add(shake);
      // A Shadow is smoke and rags: nothing lodges in it and it does not bleed.
      if (event.kind === 'impact' && !snapshot.summonedEnemies?.some(unit => unit.id === event.targetId && unit.appearance?.rig === 'shadow')) {
        if (event.ranged && !hasOwnFlight(event.attackId ?? '') && !isShepherdShot(event.attackId ?? '')) this.hits.push({ actorId: event.actorId, targetId: event.targetId, at: performance.now() });
        const target = [...snapshot.heroes, ...snapshot.enemies].find(unit => unit.id === event.targetId);
        this.blood.hit(event, {
          strength: hitStrength({ damage: event.damage, critical: event.critical }, target?.health.maxHp ?? 20),
          height: this.heights.get(event.targetId) ?? 2, radius: target?.radius ?? .5, sever: () => this.severHead(event.targetId),
        });
      }
      const dedicatedSpellCue = event.abilityId === 'breath-of-life' || event.abilityId === 'lay-on-hands' || (event.kind === 'condition' && event.label === 'TAUNTED') || isShepherdCue(event.abilityId) || isMageCue(event);
      // Dazed, Prone and Frightened are drawn from the unit's state for as long as it lasts, not as a burst.
      const persistentStatus = event.kind === 'condition' && persistentStatusLabels.has(event.label ?? '');
      if (!dedicatedSpellCue && !persistentStatus) this.addSkill(event, snapshot, snapshot.gameTime - presenceLead(event));
      const message = combatMessage(event, snapshot.heroes.some(hero => hero.id === event.actorId));
      if (message) this.messages.show(message);
      const judgment = judgmentMessage(event);
      if (judgment) this.messages.show(judgment);
    }
    // An earned CHAAARGE! stays over the rider until its next attack roll spends it.
    const charging = new Set([...snapshot.heroes, ...snapshot.enemies].filter(unit => unit.combat.charge && !unit.health.dead).map(unit => `charge:${unit.id}`));
    for (const key of this.sustained) if (!charging.has(key)) this.messages.sustain(key, undefined);
    for (const key of charging) this.messages.sustain(key, chargeMessage(key.slice(7)));
    this.sustained = charging;
  }
  /** An arrow that landed a hit stays in its target; misses, blocks and bolts of lightning just end. */
  private lodge(arrow: THREE.Group, units: { id: string; radius: number }[], positions: ReadonlyMap<string, THREE.Object3D>) {
    const { actorId, targetId } = arrow.userData as { actorId: string; targetId: string };
    const index = this.hits.findIndex(hit => hit.actorId === actorId && hit.targetId === targetId);
    const figure = positions.get(targetId), unit = units.find(candidate => candidate.id === targetId);
    if (index < 0 || !figure || !unit || (arrow.name !== 'arrow-projectile' && arrow.name !== 'javelin-projectile')) return false;
    this.hits.splice(index, 1);
    // The flight ends on the target's axis. Find where the shaft really meets the skin and bury only the head there.
    const direction = new THREE.Vector3(0, 1, 0).applyQuaternion(arrow.quaternion);
    const half = arrow.name === 'javelin-projectile' ? .6 : (arrow.userData.length as number | undefined ? (arrow.userData.length as number) / 2 + .03 : .39);
    figure.updateWorldMatrix(true, true);
    const surface = skinAlong(figure, arrow.position, direction, unit.radius + 2);
    const holder = nearestBone(figure, surface ?? arrow.position) ?? figure;
    // Without a surface to find (a figure with no solid mesh) fall back on the collision radius.
    if (surface) arrow.position.copy(surface).addScaledVector(direction, .1 - half);
    else arrow.position.addScaledVector(direction, -(half + unit.radius * .55 - .1));
    holder.attach(arrow);
    const list = this.stuck.get(targetId) ?? []; list.push(arrow); this.stuck.set(targetId, list);
    // Larger creatures carry proportionally more.
    while (list.length > Math.round(8 * Math.max(1, unit.radius / .5))) this.remove(list.shift()!);
    return true;
  }
  /** Lodged arrows leave when their figure is rebuilt, or when the unit is raised again after a reset. */
  private tidyStuck(units: { id: string; health: { dead: boolean } }[], positions: ReadonlyMap<string, THREE.Object3D>) {
    for (const unit of units) {
      if (this.wasDead.get(unit.id) && !unit.health.dead) this.dropStuck(unit.id);
      this.wasDead.set(unit.id, unit.health.dead);
    }
    for (const [id, arrows] of this.stuck) if (arrows.some(arrow => !isWithin(arrow, positions.get(id)))) this.dropStuck(id);
  }
  private dropStuck(id: string) {
    for (const arrow of this.stuck.get(id) ?? []) this.remove(arrow);
    this.stuck.delete(id);
  }
  /** Uses the same buffered snapshots/positions as the characters, including paused game time. */
  updateFrame(snapshot: WorldSnapshot, earlier = snapshot, alpha = 1, positions: ReadonlyMap<string, THREE.Object3D> = new Map(), ppm = 40, animators: ReadonlyMap<string, UnitAnimator> = new Map(), previewCasts: ReadonlyMap<string, CastState> = new Map()) {
    this.positions = positions;
    const gameTime = earlier.gameTime + (snapshot.gameTime - earlier.gameTime) * alpha;
    this.frameTime = gameTime;
    for (const [id, animator] of animators) {
      const origin = animator.spellOrigin?.(); if (origin && positions.get(id)?.visible) this.channelOrigins.set(id, { position: origin.clone(), at: gameTime });
    }
    for (const [id, origin] of this.channelOrigins) if (!positions.get(id)?.visible || gameTime - origin.at > .4) this.channelOrigins.delete(id);
    this.spells.update(snapshot, earlier, alpha, positions, ppm, animators, previewCasts);
    this.shadowBlasts.update(snapshot, earlier, alpha, positions, this.channelOrigins);
    this.shadowmancer.update(snapshot, gameTime, positions, animators);
    this.shepherd.update(snapshot, earlier, alpha, positions, ppm);
    this.berserker.update(snapshot, earlier, alpha, positions, ppm);
    this.oathsworn.update(snapshot, earlier, alpha, positions, ppm);
    this.mage.update(snapshot, earlier, alpha, positions, this.channelOrigins, ppm);
    this.messages.updateFrame(positions);
    this.targetArrows.update(snapshot, earlier, alpha, positions);
    this.statuses.update(snapshot, gameTime, positions, ppm);
    const active = new Set(snapshot.projectiles.map(shot => shot.id));
    const units = [...snapshot.heroes, ...snapshot.enemies];
    const presentUnits = new Set(units.map(unit => unit.id));
    this.hits = this.hits.filter(hit => performance.now() - hit.at < 15000);
    for (const [id, arrow] of this.arrows) if (!active.has(id)) {
      this.arrows.delete(id);
      if (!this.lodge(arrow, units, positions)) this.remove(arrow);
    }
    this.tidyStuck(units, positions);
    for (const id of this.discharged) if (!active.has(id)) this.discharged.delete(id);
    for (const shot of snapshot.projectiles) {
      if (hasOwnFlight(shot.attackId) || isShepherdShot(shot.attackId)) continue;
      if (weaponVisual({ id: shot.attackId, mode: 'ranged' }).projectile === 'lightning') {
        // The same discharge as Arc Lightning's jump, from the caster to the first target.
        if (!this.discharged.has(shot.id)) {
          this.discharged.add(shot.id);
          const origin = this.channelOrigins.get(shot.actorId)?.position;
          if (shot.misses) this.strayDischarge(shot.actorId, shot.targetId, shot.from, shot.to, snapshot, gameTime, origin, shot.id);
          else this.addSkill({ id: 0, kind: 'arc', actorId: shot.actorId, targetId: shot.targetId, from: { ...shot.from }, to: { ...shot.to }, damage: 0, critical: false }, snapshot, gameTime, origin);
        }
        continue;
      }
      let arrow = this.arrows.get(shot.id);
      if (!arrow) {
        arrow = createProjectileVisual(shot.attackId); arrow.userData = { actorId: shot.actorId, targetId: shot.targetId }; this.scene.add(arrow); this.arrows.set(shot.id, arrow);
      }
      const previous = earlier.projectiles.find(previous => previous.id === shot.id) ?? shot;
      const remaining = previous.remaining + (shot.remaining - previous.remaining) * alpha;
      const to = { x: previous.to.x + (shot.to.x - previous.to.x) * alpha, z: previous.to.z + (shot.to.z - previous.to.z) * alpha };
      const progress = shot.duration > 0 ? 1 - remaining / shot.duration : 1;
      poseProjectile(arrow, { ...shot, to }, progress, this.groundHeight(shot.from.x, shot.from.z) + (this.heights.get(shot.actorId) ?? 2) * .72, this.groundHeight(to.x, to.z) + (this.heights.get(shot.targetId) ?? 2) * .52, gameTime, ppm);
    }
    const marked = new Set(snapshot.heroes.map(hero => hero.class.quarryId).filter((id): id is string => id !== null));
    const liveQuarry = snapshot.enemies.filter(enemy => marked.has(enemy.id) && !enemy.health.dead);
    const present = new Set(liveQuarry.map(enemy => enemy.id));
    for (const [id, mark] of this.quarry) if (!present.has(id)) { this.remove(mark); this.quarry.delete(id); }
    for (const enemy of liveQuarry) {
      let mark = this.quarry.get(enemy.id);
      if (!mark) { mark = createQuarryVisual(enemy.radius, this.heights.get(enemy.id) ?? 2); this.scene.add(mark); this.quarry.set(enemy.id, mark); }
      const previous = earlier.enemies.find(unit => unit.id === enemy.id) ?? enemy;
      const position = positions.get(enemy.id)?.position ?? new THREE.Vector3(previous.x + (enemy.x - previous.x) * alpha, 0, previous.z + (enemy.z - previous.z) * alpha);
      mark.position.copy(position);
      mark.visible = positions.get(enemy.id)?.visible !== false;
      updateQuarryVisual(mark, gameTime, ppm);
    }
    let activeSkills = 0;
    this.powerLights.length = 0;
    for (const skill of this.skills) {
      const elapsed = gameTime - skill.started, p = elapsed * 1000 / skill.effect.duration;
      // An effect ends with its target leaving the world, but waits for a target the buffered snapshots do not show yet.
      const present = presentUnits.has(skill.event.targetId); skill.seen ||= present;
      if (p >= 1 || skill.seen && !present) { this.remove(skill.effect.group); continue; }
      const from = positions.get(skill.event.actorId), to = positions.get(skill.event.targetId);
      // A Sneak smoke bomb goes off while the Assassin's figure is still hidden, to cover its arrival.
      skill.effect.group.visible = p >= 0 && (to?.visible !== false || skill.event.abilityId === 'sneak') && (skill.event.kind !== 'command' && skill.event.kind !== 'mark' && skill.event.abilityId !== 'strict-training' || from?.visible !== false);
      skill.from.set(skill.event.from.x, this.groundHeight(skill.event.from.x, skill.event.from.z), skill.event.from.z);
      skill.to.set(skill.event.to.x, this.groundHeight(skill.event.to.x, skill.event.to.z), skill.event.to.z);
      if (skill.event.kind !== 'teleport') {
        if (skill.origin) skill.from.copy(skill.origin);
        else if (from) skill.from.copy(from.position);
        if (to) skill.to.copy(to.position);
        // An anchored effect emanates from a joint of its figure (a wolf's jaw), or from head height on a figure without it.
        if (skill.effect.anchor) {
          const joint = to?.getObjectByName(skill.effect.anchor);
          if (joint) joint.getWorldPosition(skill.to); else skill.to.y += (this.heights.get(skill.event.targetId) ?? 2) * .8;
        }
      }
      skill.effect.update(Math.max(0, p), skill.from, skill.to, Math.max(0, elapsed), ppm);
      if (skill.effect.group.visible && skill.effect.lights) this.powerLights.push(...skill.effect.lights);
      this.skills[activeSkills++] = skill;
    }
    this.skills.length = activeSkills;
  }
  render(camera: THREE.Camera, width = this.host.clientWidth, height = this.host.clientHeight) {
    const now = performance.now();
    this.blood.update((now - this.lastRender) / 1000); this.shake.update((now - this.lastRender) / 1000); this.lastRender = now;
    this.messages.setLevel(graphicsOptions.overheadText);
    this.messages.render(camera, width, height, now);
  }
}
